import React, { FC, useEffect, useState } from 'react';
import { Box, Chip } from '@mui/material';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { isEqual } from 'lodash';

import { CompaniesFilter, CompaniesQueryParams } from '../types';

interface Props {
  filter: CompaniesFilter;
  setFilter: (filter: CompaniesQueryParams) => void;
  isEmpty?: boolean;
}

const styles = {
  chip: {
    borderRadius: 16,
    color: 'text.secondary',
  },
  wrapper: {
    '& > div': {
      marginRight: 1,
    },
  },
} as const;

const Chips: FC<Props> = ({ filter, setFilter }) => {
  const [tempFilter, setTempFilter] = useState<CompaniesFilter>(filter);
  const hasFilters =
    !!tempFilter?.users?.length ||
    !!tempFilter?.posts?.length ||
    !!tempFilter?.company_pages?.length;

  useEffect(() => {
    if (!isEqual(filter, tempFilter)) {
      setTempFilter(filter);
    }
  }, [filter, tempFilter]);

  function onDeleteList(list: 'users' | 'posts' | 'company_pages', id: string) {
    const updatedList = filter[list].filter(item => item !== id);
    if (!updatedList.length) {
      delete tempFilter[list];
      setFilter({
        ...tempFilter,
        sort_by: JSON.stringify(filter.sort_by),
        users: tempFilter?.users?.join(',') || '',
        posts: tempFilter?.posts?.join(',') || '',
        company_pages: tempFilter?.company_pages?.join(',') || '',
      });
      return;
    }

    setFilter({
      ...tempFilter,
      sort_by: JSON.stringify(filter.sort_by),
      users: tempFilter?.users?.join(',') || '',
      posts: tempFilter?.posts?.join(',') || '',
      company_pages: tempFilter?.company_pages?.join(',') || '',
      [list]: updatedList.join(',') || '',
    });
  }

  function renderUsers() {
    if (!filter?.users) {
      return null;
    }

    return filter.users.map((user, idx) => (
      <Chip
        clickable
        size="small"
        key={user + idx}
        variant="outlined"
        label={user}
        sx={styles.chip}
        onDelete={() => onDeleteList('users', user)}
        deleteIcon={<ClearRoundedIcon fontSize="small" />}
      />
    ));
  }

  function renderPosts() {
    if (!filter?.posts) {
      return null;
    }

    return filter.posts.map((post, idx) => (
      <Chip
        clickable
        size="small"
        key={post + idx}
        variant="outlined"
        label={post}
        sx={styles.chip}
        onDelete={() => onDeleteList('posts', post)}
        deleteIcon={<ClearRoundedIcon fontSize="small" />}
      />
    ));
  }

  function renderCompanyPages() {
    if (!filter?.company_pages) {
      return null;
    }

    return filter.company_pages.map((company, idx) => (
      <Chip
        clickable
        size="small"
        key={company + idx}
        variant="outlined"
        label={company}
        sx={styles.chip}
        onDelete={() => onDeleteList('company_pages', company)}
        deleteIcon={<ClearRoundedIcon fontSize="small" />}
      />
    ));
  }

  if (!hasFilters) {
    return null;
  }

  return (
    <Box sx={styles.wrapper} marginTop={2}>
      {renderUsers()}
      {renderPosts()}
      {renderCompanyPages()}
    </Box>
  );
};

export default Chips;
